import type { PreviewTake, StageStateV1 } from '@shotstead/contracts'
import {
  THREE_RUNTIME_ADAPTER_ID,
  THREE_RUNTIME_ADAPTER_VERSION,
} from '@shotstead/stage-runtime-three'

export type PreviewCaptureResult = {
  blob: Blob
  objectUrl: string
  take: PreviewTake
}

const preferredMimeTypes = [
  'video/webm;codecs=vp9',
  'video/webm;codecs=vp8',
  'video/webm',
  'video/mp4',
]

function selectMimeType(): string | undefined {
  if (typeof MediaRecorder === 'undefined') return undefined
  return preferredMimeTypes.find((mimeType) => MediaRecorder.isTypeSupported(mimeType))
}

export async function capturePreviewTakeFromCanvas({
  canvas,
  stage,
  durationSeconds,
}: {
  canvas: HTMLCanvasElement
  stage: StageStateV1
  durationSeconds: number
}): Promise<PreviewCaptureResult> {
  if (typeof MediaRecorder === 'undefined') {
    throw new Error('MediaRecorder is not supported by this browser.')
  }

  if (typeof canvas.captureStream !== 'function') {
    throw new Error('Canvas captureStream is not supported by this browser.')
  }

  const stream = canvas.captureStream(30)
  const mimeType = selectMimeType()
  const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream)
  const chunks: BlobPart[] = []
  const executionId = crypto.randomUUID()

  return new Promise((resolve, reject) => {
    const stopTimer = window.setTimeout(() => {
      if (recorder.state !== 'inactive') recorder.stop()
    }, Math.ceil(durationSeconds * 1000) + 180)

    recorder.addEventListener('dataavailable', (event) => {
      if (event.data.size > 0) chunks.push(event.data)
    })

    recorder.addEventListener('error', (event) => {
      window.clearTimeout(stopTimer)
      stream.getTracks().forEach((track) => track.stop())
      reject(new Error(`MediaRecorder failed: ${event.error?.message ?? 'unknown error'}`))
    })

    recorder.addEventListener('stop', () => {
      window.clearTimeout(stopTimer)
      stream.getTracks().forEach((track) => track.stop())

      const finalMimeType = recorder.mimeType || mimeType || 'video/webm'
      const blob = new Blob(chunks, { type: finalMimeType })
      const objectUrl = URL.createObjectURL(blob)
      const take: PreviewTake = {
        take_id: crypto.randomUUID(),
        execution_id: executionId,
        stage_id: stage.stage_id,
        stage_version: stage.stage_version,
        camera_id: stage.camera.camera_id,
        runtime_adapter: THREE_RUNTIME_ADAPTER_ID,
        runtime_version: THREE_RUNTIME_ADAPTER_VERSION,
        captured_at: new Date().toISOString(),
        duration_seconds: durationSeconds,
        mime_type: finalMimeType,
        media_ref: objectUrl,
        warnings: [],
      }

      resolve({ blob, objectUrl, take })
    })

    try {
      recorder.start(250)
    } catch (error) {
      window.clearTimeout(stopTimer)
      stream.getTracks().forEach((track) => track.stop())
      reject(error)
    }
  })
}
